// A grid of restaurants with a name filter and a sort.
//
// Key export: `RestaurantGrid`.
// Neighbours: `SupportPicker.tsx` (the full-screen "all restaurants" view) and
// `SwitchRestaurantDialog.tsx` (the sidebar switcher) both draw it;
// `lib/recentRestaurants.ts` does the sorting.
//
// Presentational: the caller says what a pick does. Sort by frequency is offered
// only once any restaurant has been opened twice here, since before that it would
// order nothing. The sort choice is kept on this device.

import { useMemo, useState } from 'react'
import { Check, Loader2, Store } from 'lucide-react'
import { readOpenCounts, sortTeams, type SortMode } from '@/lib/recentRestaurants'

const SORT_KEY = 'synvya_support_grid_sort'

export interface GridTeam {
  id: number
  name: string
  role: string
  supportSession?: boolean
}

function roleLabelOf(team: GridTeam): string {
  return team.supportSession ? 'Support session' : team.role.charAt(0).toUpperCase() + team.role.slice(1)
}

function readSort(): SortMode {
  try {
    const v = localStorage.getItem(SORT_KEY)
    return v === 'name' || v === 'frequency' || v === 'recent' ? v : 'recent'
  } catch {
    return 'recent'
  }
}

export interface RestaurantGridProps {
  teams: readonly GridTeam[]
  /** The restaurant already open, marked and not pickable. */
  activeTeamId?: number | null
  /** The restaurant being opened, shown with a spinner; all picks are disabled while set. */
  opening: number | null
  onPick: (id: number) => void
  autoFocusFilter?: boolean
}

export function RestaurantGrid({ teams, activeTeamId = null, opening, onPick, autoFocusFilter = false }: RestaurantGridProps) {
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<SortMode>(readSort)

  const counts = useMemo(() => readOpenCounts(), [])
  const hasFrequency = Object.values(counts).some((n) => n > 1)
  // A saved "frequency" with no data behind it reads as recency.
  const mode: SortMode = sort === 'frequency' && !hasFrequency ? 'recent' : sort

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    const sorted = sortTeams(teams, mode)
    return needle ? sorted.filter((t) => t.name.toLowerCase().includes(needle)) : sorted
  }, [teams, mode, filter])

  function changeSort(next: SortMode) {
    setSort(next)
    try {
      localStorage.setItem(SORT_KEY, next)
    } catch {
      /* storage blocked: the choice lasts for this visit */
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name"
          aria-label="Filter restaurants by name"
          autoFocus={autoFocusFilter}
          className="min-w-0 flex-1 rounded-lg border border-border bg-card px-3 py-2 text-base text-foreground"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Sort
          <select
            value={mode}
            onChange={(e) => changeSort(e.target.value as SortMode)}
            aria-label="Sort restaurants"
            className="rounded-lg border border-border bg-card px-2 py-2 text-base text-foreground"
          >
            <option value="recent">Most recent</option>
            <option value="name">Name</option>
            {hasFrequency && <option value="frequency">Most used</option>}
          </select>
        </label>
      </div>

      {shown.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No restaurant matches.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {shown.map((team) => {
            const active = team.id === activeTeamId
            return (
              <li key={team.id}>
                <button
                  type="button"
                  onClick={() => onPick(team.id)}
                  disabled={opening !== null || active}
                  aria-current={active ? 'true' : undefined}
                  className={`flex h-full w-full flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:opacity-60 ${
                    active ? 'border-primary bg-primary/5' : 'border-border bg-card'
                  }`}
                >
                  <span className="flex w-full items-center justify-between">
                    <Store className="h-4 w-4 text-muted-foreground" aria-hidden />
                    {active && <Check className="h-4 w-4 text-primary" aria-label="Open now" />}
                    {opening === team.id && <Loader2 className="h-4 w-4 animate-spin text-primary" aria-label="Opening" />}
                  </span>
                  <span className="line-clamp-2 text-sm font-medium text-foreground">{team.name}</span>
                  <span className="text-xs text-muted-foreground">{roleLabelOf(team)}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export { roleLabelOf }
